import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@design-system';
import { PageLink } from '@/components';
import { VideoLightbox } from '@/components/VideoLightbox';
import { CONSUMER_STAGES } from '@/features/consumer';
import { useAppMode } from '@/hooks/use-app-mode';

import { CirclePlay, Menu } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useMemo, useState, type FunctionComponent } from 'react';
import {
  useSlideConfig,
  useSlideHistory,
  useOnboardingNavigation,
  useIsMobileMenuOpen,
  useMobileMenuActions,
  getStepDefs,
  isBaseFilteredFromFullList,
  consumerFlowReplacesSlides,
  isD2CPaywallLocked,
  ONBOARDING_SLIDES,
  SLIDE_VIDEO_URLS,
} from '@/features/client-onboarding';

import { selectClient, selectFeatureFlags, selectSession } from '@/model';
import { possessive, SessionType } from '@shared';
import { OnboardingBrandHeader } from './onboarding-brand-header';
import { ProgressStep, type ProgressStepStatus } from './progress-step';

interface StepProgress {
  name: string;
  beginningSlideIndex: number;
  lastSlideIndex: number;
  isOptional: boolean;
}

export function OnboardingHeader() {
  const client = useSelector(selectClient)!;
  const slideHistory = useSlideHistory();
  const { hideProgressStepper, slidePage } = useSlideConfig();
  const isMobileMenuOpen = useIsMobileMenuOpen();
  const { toggleMobileMenu } = useMobileMenuActions();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const videoUrl = slidePage ? SLIDE_VIDEO_URLS.get(slidePage) : undefined;

  return (
    <>
      <OnboardingBrandHeader
        extraActions={
          <>
            {videoUrl && (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="size-9 rounded-full md:size-10 xl:hidden"
                onClick={() => setShowVideoModal(true)}
                aria-label="Explain slide"
              >
                <CirclePlay />
              </Button>
            )}
            <Button
              type="button"
              size="icon"
              variant="outline"
              className={cn('size-9 rounded-full md:size-10 xl:hidden', isMobileMenuOpen && 'hidden')}
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
              data-testid="onboarding-menu-button"
            >
              <Menu />
            </Button>
          </>
        }
        progress={
          slidePage && (
            <div className={cn({ 'invisible h-0': hideProgressStepper })}>
              <ProgressStepper client={client} slideHistory={slideHistory} slidePage={slidePage} />
            </div>
          )
        }
      />
      {videoUrl && (
        <VideoLightbox videoUrl={videoUrl} isOpen={showVideoModal} onOpenChange={setShowVideoModal} />
      )}
    </>
  );
}

function ProgressStepper({
  client,
  slideHistory,
  slidePage,
}: {
  client: Client;
  slideHistory: number[];
  slidePage: FunctionComponent<ClientIdProps>;
}) {
  const featureFlags = useSelector(selectFeatureFlags);
  const { sessionType, clientStripe } = useSelector(selectSession);
  const navigation = useOnboardingNavigation({ slidePage });
  const { globalSlideIndex, relativeSlideIndex, filteredSlides } = navigation;

  const isVbShopFiltered = isBaseFilteredFromFullList(client, featureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const progressPercentage = (relativeSlideIndex / filteredSlides.length) * 100;

  // The guided flow only replaces the slides after the paywall on the D2C consumer journey.
  const isD2CAppMode = consumerFlowReplacesSlides(useAppMode(), clientStripe) && isD2C;

  const steps: StepProgress[] = useMemo(() => {
    const stepDefs = getStepDefs(client, isD2C).map(step => ({ ...step, isOptional: false }));
    // In the d2c app mode the guided flow's stages replace the slides after the paywall.
    return isD2CAppMode
      ? stepDefs.filter(step =>
          ONBOARDING_SLIDES.slice(step.beginningSlideIndex, step.lastSlideIndex + 1).some(slide =>
            filteredSlides.includes(slide),
          ),
        )
      : stepDefs;
  }, [client, isD2C, isD2CAppMode, filteredSlides]);

  const currentStep = useMemo(
    () =>
      steps.find(
        step =>
          globalSlideIndex >= step.beginningSlideIndex && globalSlideIndex <= step.lastSlideIndex,
      ),
    [steps, globalSlideIndex],
  );

  const hasVisitedStep = (step: StepProgress): boolean => {
    return slideHistory.some(
      visitedIndex =>
        visitedIndex >= step.beginningSlideIndex && visitedIndex <= step.lastSlideIndex,
    );
  };

  if (isVbShopFiltered) {
    return (
      <div>
        <div className="bg-accent h-1 w-full rounded-full">
          <div
            className="bg-primary h-1 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <TooltipProvider delayDuration={0}>
        <nav
          aria-label="Progress"
          className="w-full"
        >
          <ol className="flex space-x-2 md:space-x-8">
            {steps.map(step => {
              const isCurrentStep = step === currentStep;
              const isPastStep = globalSlideIndex > step.lastSlideIndex;
              const wasVisited = hasVisitedStep(step);
              const isCompleted = isPastStep && wasVisited && !isCurrentStep;
              const isSkipped = isPastStep && !wasVisited && step.isOptional;
              const stepPercentComplete = calcStepPercentComplete(globalSlideIndex, step);

              const isLockedByPaywall = isD2CPaywallLocked(
                ONBOARDING_SLIDES[step.beginningSlideIndex],
                filteredSlides,
                isD2C,
                clientStripe,
              );

              return (
                <li
                  key={step.name}
                  className="flex-1"
                >
                  <PageLink
                    to={ONBOARDING_SLIDES[step.beginningSlideIndex]}
                    targetProps={{ clientId: client.clientId }}
                    className={cn(
                      'group flex flex-col',
                      isLockedByPaywall && 'pointer-events-none opacity-50',
                    )}
                    onClick={e => {
                      if (isLockedByPaywall) e.preventDefault();
                    }}
                  >
                    <ProgressStep
                      name={step.name}
                      status={stepStatus({ isCurrentStep, isCompleted, isSkipped })}
                      percentComplete={stepPercentComplete}
                      badge={
                        step.isOptional && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="secondary" className="hidden md:inline-flex">
                                Optional
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs">
                              These estimates are based on default assumptions. You can optionally
                              customize them to better reflect {possessive(client.clientFirstName)}
                              specific situation.
                            </TooltipContent>
                          </Tooltip>
                        )
                      }
                    />
                  </PageLink>
                </li>
              );
            })}
            {isD2CAppMode &&
              CONSUMER_STAGES.map(stage => (
                <li key={stage} className="flex-1">
                  <div className="flex flex-col">
                    <ProgressStep name={stage} status="upcoming" />
                  </div>
                </li>
              ))}
          </ol>
        </nav>
      </TooltipProvider>
    </div>
  );
}

function stepStatus({
  isCurrentStep,
  isCompleted,
  isSkipped,
}: {
  isCurrentStep: boolean;
  isCompleted: boolean;
  isSkipped: boolean;
}): ProgressStepStatus {
  if (isCurrentStep) return 'current';
  if (isCompleted) return 'completed';
  if (isSkipped) return 'skipped';
  return 'upcoming';
}

function calcStepPercentComplete(
  slideIndex: number,
  step: { beginningSlideIndex: number; lastSlideIndex: number },
): number {
  if (slideIndex < step.beginningSlideIndex) return 0;
  if (slideIndex > step.lastSlideIndex) return 100;

  return Math.round(
    ((slideIndex - step.beginningSlideIndex) /
      (step.lastSlideIndex - step.beginningSlideIndex + 1)) *
      100,
  );
}
